import { useState, useEffect, useCallback } from 'react';
import { summaryText } from '@/lib/eventSummary';
import { Link } from 'react-router-dom';
import { dashboardApi, devicesApi, eventsApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/common/StatusIndicator';
import { Cpu, AlertTriangle, Activity, Wifi, WifiOff, AlertCircle, Radio, Brain, ShieldAlert, ShieldCheck } from 'lucide-react';
import { cn, utcDate } from '@/lib/utils';
import { format } from 'date-fns';
import { useWebSocket, useRealtimeEvent } from '@/context/WebSocketContext';
import { OnboardingChecklist } from '@/components/OnboardingChecklist';

const severityConfig = {
  high: { label: 'HÖG', color: 'bg-red-600 text-white' },
  medium: { label: 'MEDEL', color: 'bg-orange-500 text-white' },
  low: { label: 'LÅG', color: 'bg-slate-400 text-white' },
};

const methodIcons = {
  ml: Brain,
  threshold: Activity,
  consecutive: AlertTriangle,
  spike: AlertCircle,
  test: ShieldCheck,
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [devices, setDevices] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { connected } = useWebSocket();

  const loadData = useCallback(async () => {
    try {
      const [statsRes, devicesRes, eventsRes] = await Promise.all([
        dashboardApi.getStats(),
        devicesApi.list({}),
        eventsApi.list({ limit: 10 }),
      ]);
      setStats(statsRes.data);
      setDevices(devicesRes.data);
      setRecentEvents(eventsRes.data);
    } catch (error) {
      console.error('Kunde inte ladda översiktsdata:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  useRealtimeEvent('dashboard_update', useCallback((data) => {
    if (data.stats) setStats(data.stats);
  }, []));

  useRealtimeEvent('new_event', useCallback((data) => {
    if (data.event) {
      setRecentEvents(prev => [data.event, ...prev.slice(0, 9)]);
      loadData();
    }
  }, [loadData]));

  useRealtimeEvent('device_status', useCallback((data) => {
    if (data.device) {
      setDevices(prev => prev.map(d => 
        d.id === data.device.id ? { ...d, ...data.device } : d
      ));
    }
  }, []));

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-slate-500 font-ibm">Laddar översikt...</div>
      </div>
    );
  }

  const total = stats?.total_devices || 0;
  const online = stats?.online_devices || 0;
  const warnings = stats?.active_warnings || 0;
  const le = stats?.latest_event;
  const leTime = le?.created_at ? format(utcDate(le.created_at), 'HH:mm') : null;

  const statCards = [
    {
      key: 'online',
      label: 'Sensorer online',
      value: `${online}/${total}`,
      icon: Wifi,
      color: online === total && total > 0 ? 'text-emerald-600' : 'text-amber-600',
    },
    {
      key: 'events-today',
      label: 'Händelser idag',
      value: stats?.total_events_today || 0,
      icon: Activity,
      color: 'text-navy',
    },
    {
      key: 'warnings',
      label: 'Aktiva driftvarningar',
      value: warnings,
      icon: AlertTriangle,
      color: warnings > 0 ? 'text-amber-600' : 'text-emerald-600',
      highlight: warnings > 0,
    },
    {
      key: 'offline',
      label: 'Offline',
      value: stats?.offline_devices || 0,
      icon: WifiOff,
      color: 'text-slate-500',
      highlight: (stats?.offline_devices || 0) > 0,
    },
  ];

  return (
    <div className="p-6 md:p-10 space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="font-chivo font-black text-2xl md:text-3xl text-navy tracking-tight">
          Översikt
        </h1>
        <p className="text-slate-500 mt-1 font-ibm">
          {le
            ? <>Senaste händelse: <span className="font-semibold text-slate-700">{le.room_name || le.device_name || 'Okänd plats'}{leTime ? ` – ${leTime}` : ''}</span></>
            : 'Realtidsöversikt av ditt vape-detektionssystem'}
        </p>
      </div>

      <OnboardingChecklist />

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card
              key={stat.key}
              data-testid={`kpi-${stat.key}`}
              className={cn(
                "border-slate-200",
                stat.highlight && stat.color === 'text-amber-600' && "border-amber-200 bg-amber-50",
                stat.highlight && stat.color === 'text-slate-500' && "border-slate-300 bg-slate-100"
              )}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-ibm uppercase tracking-widest text-slate-500">
                      {stat.label}
                    </p>
                    <p className={cn("font-chivo font-black text-3xl mt-1", stat.color)}>
                      {stat.value}
                    </p>
                  </div>
                  <Icon className={cn("h-8 w-8", stat.color)} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Severity Stats + Vape Alerts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className={cn("border-red-300", (stats?.vape_alerts_today || 0) > 0 ? "bg-red-50" : "bg-white")} data-testid="vape-alerts-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-ibm uppercase tracking-widest text-red-500">Vape-larm idag</p>
                <p className="font-chivo font-black text-3xl mt-1 text-red-600">{stats?.vape_alerts_today || 0}</p>
              </div>
              <ShieldAlert className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card className={cn("border-red-200", (stats?.high_severity_today || 0) > 0 ? "bg-red-50/60" : "bg-white")} data-testid="high-severity-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-ibm uppercase tracking-widest text-red-500">Hög allvarlighetsgrad</p>
                <p className="font-chivo font-black text-3xl mt-1 text-red-600">{stats?.high_severity_today || 0}</p>
                <p className="text-xs text-slate-400 mt-0.5 font-ibm">Idag</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-red-600 flex items-center justify-center">
                <span className="text-white font-bold text-xs">HÖG</span>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={cn("border-orange-200", (stats?.medium_severity_today || 0) > 0 ? "bg-orange-50/60" : "bg-white")} data-testid="medium-severity-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-ibm uppercase tracking-widest text-orange-500">Medel allvarlighetsgrad</p>
                <p className="font-chivo font-black text-3xl mt-1 text-orange-600">{stats?.medium_severity_today || 0}</p>
                <p className="text-xs text-slate-400 mt-0.5 font-ibm">Idag</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-orange-500 flex items-center justify-center">
                <span className="text-white font-bold text-xs">MED</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Devices List */}
        <Card className="lg:col-span-2 border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-chivo text-lg">Enhetsstatus</CardTitle>
              <Link 
                to="/devices" 
                className="text-sm text-navy hover:underline font-ibm"
                data-testid="view-all-devices"
              >
                Visa alla
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {devices.slice(0, 6).map((device) => (
                <Link
                  key={device.id}
                  to={`/devices/${device.id}`}
                  className="flex items-center justify-between p-3 rounded-sm border border-slate-100 hover:bg-slate-50 transition-colors"
                  data-testid={`device-row-${device.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <StatusBadge status={device.status} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="font-ibm font-medium text-slate-900 truncate">
                          {device.name || device.device_eui}
                        </p>
                        {device.connection_type === 'wifi' ? (
                          <Wifi className="h-3 w-3 text-purple-500 shrink-0" />
                        ) : (
                          <Radio className="h-3 w-3 text-sky-500 shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate">
                        {device.school_name} - {device.room_name}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-ibm font-semibold text-sm tabular-nums">
                      {(device.last_score * 100).toFixed(0)}%
                    </p>
                    <p className="text-xs text-slate-500">
                      {device.last_seen 
                        ? format(utcDate(device.last_seen), 'HH:mm')
                        : 'Aldrig'
                      }
                    </p>
                  </div>
                </Link>
              ))}
              {devices.length === 0 && (
                <div className="text-center py-8 text-slate-500 font-ibm">
                  Inga enheter registrerade. Lägg till en skola och ett rum först.
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Events */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-chivo text-lg">Senaste händelser</CardTitle>
              <Link 
                to="/events" 
                className="text-sm text-navy hover:underline font-ibm"
                data-testid="view-all-events"
              >
                Visa alla
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentEvents.map((event) => {
                const sevConfig = severityConfig[event.severity];
                const MethodIcon = methodIcons[event.detection_method] || Activity;
                return (
                  <div
                    key={event.id}
                    className={cn(
                      "p-3 rounded-sm border",
                      event.severity === 'high' && "border-red-300 bg-red-50",
                      event.severity === 'medium' && "border-orange-200 bg-orange-50",
                      !event.severity && event.event_type === 'vape' && "border-red-200 bg-red-50",
                      !event.severity && event.event_type === 'suspect' && "border-amber-200 bg-amber-50",
                      event.event_type === 'offline' && "border-slate-200 bg-slate-50",
                      event.event_type === 'normal' && "border-emerald-200 bg-emerald-50",
                      event.event_type === 'test_alarm' && "border-purple-200 bg-purple-50"
                    )}
                    data-testid={`event-${event.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className={cn(
                            "text-xs font-ibm font-bold uppercase tracking-wide",
                            event.event_type === 'vape' && "text-red-700",
                            event.event_type === 'suspect' && "text-amber-700",
                            event.event_type === 'offline' && "text-slate-600",
                            event.event_type === 'test_alarm' && "text-purple-700"
                          )}>
                            {event.event_type === 'test_alarm' ? 'TESTLARM' : 
                             event.event_type === 'vape' ? 'VAPE' :
                             event.event_type === 'suspect' ? 'MISSTÄNKT' :
                             event.event_type === 'offline' ? 'OFFLINE' :
                             event.event_type.toUpperCase()}
                          </span>
                          {sevConfig && (
                            <span className={cn("text-[9px] font-bold uppercase px-1 py-0 rounded", sevConfig.color)}>
                              {sevConfig.label}
                            </span>
                          )}
                          {event.detection_method === 'ml' && (
                            <Brain className="h-3 w-3 text-violet-500" />
                          )}
                        </div>
                        <p className="text-sm text-slate-700 mt-0.5 truncate">
                          {event.device_name}
                        </p>
                      </div>
                      <span className="text-xs text-slate-500 tabular-nums shrink-0">
                        {event.created_at ? format(utcDate(event.created_at), 'HH:mm') : ''}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                      {summaryText(event)}
                    </p>
                  </div>
                );
              })}
              {recentEvents.length === 0 && (
                <div className="text-center py-8 text-slate-500 font-ibm">
                  Inga händelser än
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
